import { kebabCase } from 'lodash';
import { createContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Breadcrumb, HeroBanner, OffersCategories, OffersList, SanitizedHtml } from 'components';
import { useFetch, useSafeState } from 'hooks';
import { routes } from 'router';
import { appService } from 'services';

import 'assets/styles/components/pages/offers.scss';

export const OffersPageContext = createContext();

const OffersPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { call: offersCategoriesCall, data: offersCategoriesData, loading: offersCategoriesLoading } = useFetch();
  const { call: offersCall, data: offersData, loading: offersLoading } = useFetch({ filterUserCompany: true });

  const [selectedCategory, setSelectedCategory] = useSafeState(null);

  useEffect(() => {
    getOffersCategories();
    getOffers();
  }, []);

  useEffect(() => {
    if (searchParams.get('category')) {
      setSelectedCategory(searchParams.get('category'));
    } else {
      const initialCategory = offersCategoriesData?.data?.find((offer) => offer.order === 1)?.name;

      if (initialCategory) {
        setSearchParams({ ...searchParams, category: kebabCase(initialCategory) });
      }
    }
  }, [offersCategoriesData, searchParams]);

  const getOffersCategories = () => {
    offersCategoriesCall({
      service: appService.getStrapiServiceName(),
      path: '/api/offer-categories',
      method: 'get'
    });
  };

  const getOffers = () => {
    offersCall({
      service: appService.getStrapiServiceName(),
      path: '/api/offer-pages',
      method: 'get'
    });
  };

  const breadcrumbItems = [
    { url: routes.home.path, label: t('breadcrumb.home') },
    { label: offersData?.data?.[0]?.heroTitle || '' }
  ];

  return (
    <OffersPageContext.Provider
      value={{
        offersCategoriesData,
        offersCategoriesLoading,
        offersData,
        offersLoading,
        selectedCategory,
        setSelectedCategory
      }}
    >
      <div className="offers-container bg-gray-50 lg:bg-white">
        <HeroBanner
          title={offersData?.data?.[0]?.heroTitle || ''}
          image={offersData?.data?.[0]?.heroImageDesktop}
          mobileImage={offersData?.data?.[0]?.heroImageMobile}
          loading={offersLoading}
        />

        <div className="main-container md:mt-20">
          <div className="hidden md:block">
            <Breadcrumb breadcrumbItems={breadcrumbItems} />
          </div>

          <SanitizedHtml content={offersData?.data?.[0]?.introText} className="pt-4 pb-8" />

          <OffersCategories />
          <OffersList />
        </div>
      </div>
    </OffersPageContext.Provider>
  );
};

export default OffersPage;
